import React, { useEffect, useRef, useState } from 'react'
import { useCustomizeUI } from 'src/lib/access-permission-hooks/useCustomizeUI'
import { DeviceType, PreviewContainer } from 'src/components/ui-customization/preview-layout/PreviewLayout'
import { ArrowLeft } from '@phosphor-icons/react'
import { Icon } from '@shopflo/ui'
import Pill from 'src/components/common/ui-preview/skeletons/Pill'
import usePaymentPage from 'src/lib/contexts/PaymentPageContext'
import PreviewBanner from 'src/components/common/ui-preview/sub-components/PreviewBanner'
import PaymentMethodsPreview from './PaymentMethodsPreview'
import SubscriptionPaymentTilesPreview from './SubscriptionPaymentTilesPreview'
import ChoosePlanSheet from 'src/components/subscriptions/preview/ChoosePlanSheet'
import SubscriptionPreviewBlock from './SubscriptionPreviewBlock'
import PurchaseModeTabsPreview from './PurchaseModeTabsPreview'
import { useSubscriptionPermissions } from 'src/lib/access-permission-hooks/useSubscriptionPermissions'
import { SHEET_PLANS } from './subscriptionPreviewData'
import { classNames } from 'src/lib/utils/helpers'
import PreviewCoupons from 'src/components/common/ui-preview/sub-components/PreviewCoupons'
import { UIPreviewPaymentMethodType } from 'src/lib/types/uiPreview'

interface PaymentPagePreviewProps {
  isPaymentOfferPreview: boolean
  config?: {
    paymentMethods?: UIPreviewPaymentMethodType[]
  }
}

const PaymentPagePreview: React.FC<PaymentPagePreviewProps> = ({ isPaymentOfferPreview = false, config }) => {
  const [isPlanSheetOpen, setIsPlanSheetOpen] = useState(false)

  const {
    getters: { bannerConfig, selection, couponVisibilityConfig, purchaseMode, subscriptionConfig },
    actions: { setSelection }
  } = usePaymentPage()

  const { canViewSubscriptions } = useSubscriptionPermissions()
  const isSubscribe = canViewSubscriptions && purchaseMode === 'SUBSCRIBE'

  const bannerRef = useRef<HTMLDivElement>(null)
  const couponRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (selection.type === 'BANNER') {
      bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    if (selection.type === 'COUPON') {
      couponRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [selection])

  return (
    <div
      className='h-fit py-3  px-12 flex mt-4 '
      onClick={e => {
        e.stopPropagation()
      }}
      ref={previewRef}
    >
      <PreviewContainer device={DeviceType.MOBILE}>
        <div className='sticky top-0  bg-white pt-2 z-20'>
          <div className='flex justify-between px-2 pt-1 pb-3'>
            <div className='flex items-center gap-2'>
              <Icon IconComponent={ArrowLeft} size='sm' className='text-zinc-800' />
              <Pill height={1.5} width={5} customCSS={{ borderRadius: 24 }} />
            </div>
            <div className='flex flex-col gap-2'>
              <Pill height={1} width={6} customCSS={{ borderRadius: 12 }} />
              <div className='flex items-center gap-2 justify-end'>
                <Pill height={1} width={2} customCSS={{ borderRadius: 12 }} />
                <Pill height={1} width={2} customCSS={{ borderRadius: 12 }} />
              </div>
            </div>
          </div>

          {bannerConfig.enabled && !isPaymentOfferPreview && (
            <div
              className={classNames(
                'w-full',
                selection.type === 'METHOD' && selection.id ? 'opacity-50' : '',
                selection.type === 'BANNER' ? 'border-2 border-coal-dark rounded' : ''
              )}
              onClick={e => {
                e.stopPropagation()
                setSelection({ type: 'BANNER', id: null })
              }}
              ref={bannerRef}
            >
              <PreviewBanner
                backgroundColor={bannerConfig.backgroundColor ?? '#5a31f4'}
                textColor={bannerConfig.textColor ?? '#FFFFFF'}
                bannerText={bannerConfig.htmlTextString ?? ''}
                cartBannerFlags={{
                  isCartBottomBanner: false
                }}
              />
            </div>
          )}
        </div>

        {couponVisibilityConfig.areCouponsVisible && !isSubscribe && !isPaymentOfferPreview && (
          <div
            className={classNames(
              selection.type === 'METHOD' && selection.id ? 'opacity-50' : '',
              selection.type === 'COUPON' ? 'border-2 border-coal-dark rounded' : ''
            )}
            onClick={e => {
              e.stopPropagation()
              setSelection({ type: 'COUPON', id: null })
            }}
            ref={couponRef}
          >
            <PreviewCoupons type={couponVisibilityConfig.visibilityExtent} />
          </div>
        )}

        {canViewSubscriptions && !isPaymentOfferPreview && <PurchaseModeTabsPreview />}

        {isSubscribe && !isPaymentOfferPreview && (
          <div
            className={classNames(
              selection.type === 'BANNER' || selection.type === 'COUPON' ? 'opacity-50' : '',
              'pb-2 flex flex-col'
            )}
          >
            <SubscriptionPreviewBlock onMorePlansClick={() => setIsPlanSheetOpen(true)} />
          </div>
        )}

        {isSubscribe && !isPaymentOfferPreview ? (
          <SubscriptionPaymentTilesPreview />
        ) : (
          <div
            className={classNames(
              selection.type === 'BANNER' || selection.type === 'COUPON' ? 'opacity-50' : '',
              'py-2 gap-2 flex flex-col'
            )}
          >
            <h2 className='text-text-xs-medium text-coal-light px-4'>Payment Methods</h2>
            <PaymentMethodsPreview
              paymentMethodsConfig={config?.paymentMethods}
              isPaymentOfferPreview={isPaymentOfferPreview}
            />
          </div>
        )}
        {isPlanSheetOpen && isSubscribe && (
          <div className='sticky bottom-0 z-20 h-full'>
            <ChoosePlanSheet
              plans={SHEET_PLANS}
              selectedLocalId={SHEET_PLANS[0]?.localId}
              onSelect={() => setIsPlanSheetOpen(false)}
              onClose={() => setIsPlanSheetOpen(false)}
              colors={{
                offer: subscriptionConfig.layoutUi.subscriptionCard.offerColor,
                highlight: subscriptionConfig.layoutUi.subscriptionCard.highlightBackgroundColor,
                icon: subscriptionConfig.layoutUi.subscriptionCard.iconColor
              }}
            />
          </div>
        )}
      </PreviewContainer>
    </div>
  )
}

export default PaymentPagePreview
